import { groupBy } from 'lodash';

import { InfoBlockWrapper, ExpenseItem, Toggle } from 'components';
import { moneyFormatter, getExpenseItemsSum } from 'utils';

import type { ExpensesBreakdown } from 'types';
import { PieChart } from 'react-minimal-pie-chart';

import { ChevronDown, ChevronUp } from 'assets/icons';

type EmployeeIncomeInfoProps = {
  employeeGets: ExpensesBreakdown;
  timeFrameMode: 'annual' | 'monthly';
};

const defaultLabelStyle = {
  fontSize: '5px',
  fontFamily: 'sans-serif',
};

export const EmployeeIncomeInfo = ({
  employeeGets,
  timeFrameMode,
}: EmployeeIncomeInfoProps) => {
  const { gross, taxes, total, taxesBreakdown } = employeeGets;
  const taxGroups = groupBy(taxesBreakdown, 'group');
  const taxGroupsEntries = Object.entries(taxGroups);

  return (
    <InfoBlockWrapper>
      <h2 className="font-semibold pb-2 border-b border-e3 mb-4">
        What your team member will get:
      </h2>

      <ExpenseItem
        title={`Gross ${
          timeFrameMode === 'monthly' ? 'monthly' : 'annual'
        } salary`}
        sum={moneyFormatter(gross)}
      />
      <Toggle>
        {(isOpened, toggle) => (
          <>
            <ExpenseItem
              title={
                <div className="select-none flex items-center gap-[6px]">
                  Taxes & social security
                  {isOpened ? (
                    <ChevronUp className="self-baseline	shrink-0" />
                  ) : (
                    <ChevronDown className="self-baseline	shrink-0" />
                  )}
                </div>
              }
              sum={moneyFormatter(taxes)}
              bold
              onClick={toggle}
            />

            {isOpened && (
              <div className="py-2 px-3 border border-e3 rounded-md mb-4">
                {taxGroupsEntries.map(([title, points]) => {
                  return (
                    <div className="mb-4" key={title}>
                      <label className="text-sm font-semibold select-none flex items-center gap-[6px]">
                        {title}
                        <div className="ml-auto">
                          {moneyFormatter(getExpenseItemsSum(points))}
                        </div>
                      </label>
                      <div className="pl-4 mt-2">
                        {points.map((expense, i, arr) => (
                          <ExpenseItem
                            key={expense.name}
                            title={expense.name}
                            sum={moneyFormatter(expense.amount)}
                            disableMargin={arr.length - 1 === i}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Toggle>
      <ExpenseItem
        title={`Net ${
          timeFrameMode === 'monthly' ? 'monthly' : 'annual'
        } salary`}
        sum={moneyFormatter(total)}
        bold
      />

      <div>
        <PieChart
          style={{ height: '220px', marginTop: '60px', overflow: 'visible' }}
          startAngle={200}
          labelPosition={60}
          totalValue={gross}
          animate
          label={({ x, y, dx, dy, dataEntry }) => {
            return (
              <>
                <text
                  x={x}
                  y={y}
                  dx={dx}
                  dy={dy}
                  dominantBaseline="central"
                  textAnchor="middle"
                >
                  <tspan x={x} y={y} dx={dx} dy={dy} style={{ fontSize: 6 }}>
                    {dataEntry.title}
                  </tspan>
                  <tspan
                    x={x}
                    y={y + 8}
                    dx={dx}
                    dy={dy}
                    style={{ fontSize: 6, fontWeight: 600 }}
                  >
                    {dataEntry.percentage.toFixed(2)}%
                  </tspan>
                </text>
              </>
            );
          }}
          data={[
            {
              title: 'The tax employee pays',
              value: taxes,
              color: '#d6f1d6',
            },
            {
              title: 'Net Salary',
              value: total,
              color: '#abe3aa',
            },
          ]}
          labelStyle={defaultLabelStyle}
        />

        <div className="text-sm my-8">
          *An income tax of{' '}
          <span className="font-semibold">{moneyFormatter(taxes)}</span> will be
          deducted from your gross salary of{' '}
          <span className="font-semibold">{moneyFormatter(gross)}</span>. Your
          net (take home) salary will be{' '}
          <span className="font-semibold">{moneyFormatter(total)}</span>.
        </div>
      </div>
    </InfoBlockWrapper>
  );
};
